import React, { useCallback, useEffect, useRef, useState } from "react";
import { Check, ChevronLeft, ChevronRight, FileText, Plus, RefreshCw, RotateCcw, Send, X } from "lucide-react";
import { Groupe, TitreGroupe, Vide, Badge, Segments } from "../espace/ui";
import { api, erreurDe, chf, jourCourt, moisCourant, decalerMois, moisLisible, urlServeur, aujourdhui } from "./commun";

// Un cours pas encore donné (plus tard aujourd'hui, ou un jour suivant) : on ne le valide pas.
const aVenir = (l) => {
  const auj = aujourdhui();
  if (l.date !== auj) return l.date > auj;
  return !!l.fin && l.fin > new Date().toTimeString().slice(0, 5);
};
const ajoutee = (l) => l.type === "cours privé" || l.type === "ajout";
// Une correction d'un mois déjà validé, reportée par l'école sur ce mois (Jen, 02.11.2026) :
// rien à cocher, elle compte dans le total.
const estRegul = (l) => l.type === "régularisation";
// « CHF −20.00 », « CHF +20.00 » : le signe d'une correction se lit d'un coup d'œil.
const chfSigne = (n) => `CHF ${Number(n) < 0 ? "−" : Number(n) > 0 ? "+" : ""}${Math.abs(Number(n || 0)).toFixed(2)}`;
// Une séance sans aucun élève : le cours n'a pas eu lieu, 0.– — rien à cocher (comme
// profs.sans_eleve côté serveur ; 6 cours vides sur 8 à cocher, tests du 01.11.2026).
const sansEleve = (l) => ["cours", "remplacement"].includes(l.type) && !l.eleves && !l.minutes && !Number(l.montant || 0);
// « 9 élèves (7.5 au barème) » : une carte 10 cours compte ½ dans le barème, mais le prof
// voit chaque personne (comme dans Mes cours).
const eleves = (l) => {
  const n = l.personnes ?? l.eleves;
  const bareme = l.personnes != null && l.personnes !== l.eleves ? ` (${l.eleves} au barème : carte 10 cours = ½)` : "";
  return `${n} élève${n > 1 ? "s" : ""}${bareme}`;
};

const enAjout = (l, prives) => ({
  date: l.date, libelle: l.cours, cle: l.cle, debut: l.debut || "", fin: l.fin || "",
  eleves: l.eleves || 0, minutes: l.type === "cours privé" ? 0 : (l.debut ? 0 : l.minutes),
  montant: 0, cours_prive: l.type === "cours privé" ? (prives.find((c) => c.label === l.cours)?.id || "") : "",
});

const tarifEstime = (nb, bareme, fixe) => {
  if (nb <= 0) return 0;
  if (fixe) return fixe;
  const t = bareme.find(([seuil]) => nb >= seuil);
  return t ? t[1] : 0;
};
const enMin = (h) => { const [x, y] = String(h || "").split(":").map(Number); return (x || 0) * 60 + (y || 0); };
const enHeure = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const duree = (a, b) => Math.max(0, enMin(b) - enMin(a));
/* Un cours qui n'est pas dans la liste : cours privé (tarif du site) ou autre (répétition,
   remplacement non prévu…) payé au barème. */
const Ajouter = ({ moi, mois, onAjout, onFermer }) => {
  const [genre, setGenre] = useState("prive");
  const [f, setF] = useState({ date: `${mois}-01`, cours_prive: moi.cours_prives[0]?.id, libelle: "", debut: "", fin: "", eleves: "" });
  const taux = tarifEstime(Number(f.eleves || 0), moi.bareme || [], moi.tarif_fixe);
  const min = duree(f.debut, f.fin);
  const ok = f.date.startsWith(mois) && f.date <= aujourdhui() && (genre === "prive" ? f.cours_prive : f.libelle.trim() && min > 0 && Number(f.eleves) > 0);
  return (
    <Groupe>
      <div className="p-4 space-y-3" data-testid="prof-ajout">
        <div className="text-[16px] font-semibold">Ajouter un cours</div>
        <Segments testId="prof-ajout-genre" valeur={genre} onChange={setGenre}
          options={[{ id: "prive", label: "Cours privé" }, { id: "autre", label: "Autre cours" }]} />
        <label className="block text-[12.5px] text-[var(--muted)]">Date
          <input type="date" className="input-field w-full mt-1" value={f.date} min={`${mois}-01`}
            max={[`${mois}-31`, aujourdhui()].sort()[0]}
            onChange={(e) => setF({ ...f, date: e.target.value })} />
        </label>
        {genre === "prive" ? (
          <label className="block text-[12.5px] text-[var(--muted)]">Formule
            <select className="input-field w-full mt-1" value={f.cours_prive} onChange={(e) => setF({ ...f, cours_prive: e.target.value })}
              data-testid="prof-ajout-type">
              {moi.cours_prives.map((c) => <option key={c.id} value={c.id}>{c.label} — {c.montant}.–</option>)}
            </select>
          </label>
        ) : (
          <>
            <input className="input-field w-full" placeholder="Quoi ? (répétition spectacle, remplacement…)" value={f.libelle}
              onChange={(e) => setF({ ...f, libelle: e.target.value })} data-testid="prof-ajout-libelle" />
            <div className="grid grid-cols-3 gap-2 text-[12.5px] text-[var(--muted)]">
              <label>De<input type="time" className="input-field w-full mt-1" value={f.debut} onChange={(e) => setF({ ...f, debut: e.target.value })} /></label>
              <label>À<input type="time" className="input-field w-full mt-1" value={f.fin} onChange={(e) => setF({ ...f, fin: e.target.value })} /></label>
              <label>Élèves<input inputMode="numeric" className="input-field w-full mt-1" value={f.eleves} onChange={(e) => setF({ ...f, eleves: e.target.value.replace(/\D/g, "") })} /></label>
            </div>
            {min > 0 && Number(f.eleves) > 0 && (
              moi.tarif_fixe && moi.tarif_fixe_par === "cours"
                ? <p className="text-[13px] text-[var(--muted)]">1 cours × {taux}.– = <strong>{chf(taux)}</strong> (ton tarif par cours)</p>
                : <p className="text-[13px] text-[var(--muted)]">{(min / 60).toFixed(2)} h × {taux}.–/h = <strong>{chf(taux * min / 60)}</strong> (barème)</p>
            )}
          </>
        )}
        <div className="flex gap-2">
          <button className="btn-primary flex-1 justify-center" disabled={!ok} data-testid="prof-ajout-valider"
            onClick={() => onAjout(genre === "prive"
              ? { date: f.date, cours_prive: f.cours_prive, libelle: "", eleves: 1 }
              : { date: f.date, libelle: f.libelle.trim(), debut: f.debut, fin: f.fin, eleves: Number(f.eleves) })}>
            <Plus size={15} /> Ajouter
          </button>
          <button className="btn-ghost" onClick={onFermer}>Annuler</button>
        </div>
      </div>
    </Groupe>
  );
};

export const Heures = ({ moi, moisDepart = "" }) => {
  // Le mois qui attend le prof d'abord (« Tes heures d'octobre sont à envoyer ») : début
  // novembre, il ouvrait novembre et le prof ne trouvait pas octobre (01.11.2026).
  const [mois, setMois] = useState(() => moisDepart
    || (moi.heures_a_verifier || []).find((x) => x.termine)?.mois || moisCourant());
  const [d, setD] = useState(null);
  const [confirmes, setConfirmes] = useState([]);
  const [retires, setRetires] = useState([]);
  const [ajout, setAjout] = useState(false);
  // L'alternance (hip-hop du mercredi) : les séances du collègue, en grisé (Jen, 02.11.2026).
  const [tours, setTours] = useState([]);
  const [annuler, setAnnuler] = useState(null);
  const [message, setMessage] = useState("");
  const [erreur, setErreur] = useState("");

  // La sauvegarde automatique en attente (minuterie) et celle en vol (promesse) :
  // « Envoyer » les attend, sinon un brouillon arrivé après l'envoi le défaisait.
  const minuterie = useRef(null);
  const enVol = useRef(Promise.resolve());
  // Cours partagé : la vraie durée de sa part, annoncée par le prof ({clé: minutes}) —
  // une proposition pour l'école (horaires partagés, version de test d'abord).
  const partages = !!moi.chantiers?.horaires_partages;
  const durees = useRef({});
  const [, redessiner] = useState(0);
  const lire = (data) => {
    setD(data);
    durees.current = Object.fromEntries(data.lignes.filter((l) => l.prevu != null).map((l) => [l.cle, l.minutes]));
    setConfirmes(data.lignes.filter((l) => l.confirme && !estRegul(l)).map((l) => l.cle));
    // Seules les lignes que LE PROF a retirées sont les siennes ; celles retirées par
    // l'école restent affichées, en lecture seule.
    setRetires(data.lignes.filter((l) => l.retire && l.retire_par !== "école").map((l) => l.cle));
  };
  const charger = useCallback(() => {
    setD(null); setMessage(""); setErreur(""); setAjout(false);
    api.get(`/heures/${mois}`).then(({ data }) => { lire(data); setTours(data.autres_tours || []); }).catch((e) => setErreur(erreurDe(e)));
  }, [mois]);
  useEffect(charger, [charger]);

  const corps = (c, r, nouveaux = []) => ({
    confirmes: c, retires: r, ...(partages ? { durees: durees.current } : {}),
    ajouts: [...d.lignes.filter((l) => l.type === "cours privé" || l.type === "ajout").map((l) => enAjout(l, moi.cours_prives)), ...nouveaux],
  });
  // Chaque ✓ / ✗ est sauvegardé tout de suite (brouillon), rien n'est perdu si l'app se ferme.
  // Le décompte relu ensuite montre ce que l'école a enregistré (sauf si d'autres coches
  // attendent déjà leur tour).
  const sauver = (c, r) => {
    clearTimeout(minuterie.current);
    minuterie.current = setTimeout(() => {
      minuterie.current = null;
      enVol.current = api.post(`/heures/${mois}/brouillon`, corps(c, r))
        .then(({ data }) => { if (!minuterie.current) lire(data); })
        .catch((e) => setErreur(erreurDe(e)));
    }, 700);
  };
  useEffect(() => () => clearTimeout(minuterie.current), [mois]);
  // « Je l'ai donné » : une séance d'alternance donnée à la place du collègue. Proposition à
  // l'école ; elle compte dans les heures une fois que l'école a basculé la séance.
  const reprendre = async (t, annuler = false) => {
    setErreur("");
    try {
      const { data } = annuler ? await api.post(`/heures/${mois}/reprise/annuler`, { cle: t.cle })
        : await api.post(`/heures/${mois}/reprise`, { cle: t.cle });
      setTours(data.autres_tours || []);
      setMessage(annuler ? "" : data.message || "");
    } catch (e) { setErreur(erreurDe(e)); }
  };
  // « Ma part du cours » : une durée par cours (Jen, 02.11.2026 : « on garde chaque cours
  // séparé » — la « soirée » du 01.11 déroutait sur un cours unique).
  const annoncer = (l, minutes) => {
    const prevu = l.prevu ?? l.minutes;
    const suite = { ...durees.current };
    if (minutes === prevu) delete suite[l.cle]; else suite[l.cle] = minutes;
    durees.current = suite;
    redessiner((n) => n + 1);
    sauver(confirmes, retires);
  };

  const valide = d?.statut === "valide";
  // Avant l'application (Réglages de l'école) : heures faites à la main, rien à cocher ni
  // à envoyer — sauf un mois que l'école a quand même validé (sa fiche reste lisible).
  const des = moi.heures_app_des || "";
  const premier = !!des && mois <= des;
  const horsApp = !!d?.hors_app && !valide;
  const marquer = (l, quoi) => {
    let c = confirmes.filter((x) => x !== l.cle);
    let r = retires.filter((x) => x !== l.cle);
    if (quoi === "ok" && !confirmes.includes(l.cle)) c = [...c, l.cle];
    if (quoi === "non" && !retires.includes(l.cle)) { r = [...r, l.cle]; setAnnuler(l); setTimeout(() => setAnnuler((a) => (a === l ? null : a)), 6000); }
    setConfirmes(c); setRetires(r); sauver(c, r);
  };
  const ajouter = async (a) => {
    try {
      const { data } = await api.post(`/heures/${mois}/brouillon`, corps(confirmes, retires, [a]));
      lire(data); setAjout(false);
    } catch (e) { setErreur(erreurDe(e)); }
  };
  const envoyer = async () => {
    setErreur("");
    // L'envoi contient déjà les dernières coches : la sauvegarde en attente est inutile,
    // et celle déjà partie doit arriver AVANT l'envoi.
    clearTimeout(minuterie.current); minuterie.current = null;
    await enVol.current;
    try {
      const { data } = await api.post(`/heures/${mois}/confirmer`, corps(confirmes, retires));
      lire(data);
      setMessage("Merci ! Tes heures sont envoyées à l'école. Tu recevras ta fiche dès qu'elle est validée.");
    } catch (e) { setErreur(erreurDe(e)); }
  };

  const reguls = (d?.lignes || []).filter(estRegul);
  const toutes = (d?.lignes || []).filter((l) => !estRegul(l));
  const parEcole = (l) => l.retire && l.retire_par === "école";
  // Seuls les cours déjà donnés se valident ; ceux à venir sont listés à part, sans bouton.
  const futures = valide ? [] : toutes.filter((l) => !ajoutee(l) && aVenir(l));
  // Les cours sans élève (0.–) : à part, repliés, sans bouton.
  const vides = toutes.filter((l) => !futures.includes(l) && sansEleve(l) && !l.corrige_ecole);
  const lignes = toutes.filter((l) => !futures.includes(l) && !vides.includes(l));
  const comptees = lignes.filter((l) => !retires.includes(l.cle) && !parEcole(l));
  const totalReguls = reguls.reduce((s, l) => s + Number(l.montant || 0), 0);
  const total = comptees.reduce((s, l) => s + Number(l.montant || 0), 0) + totalReguls;
  const heures = comptees.reduce((s, l) => s + Number(l.minutes || 0), 0) / 60;
  const aVerifier = lignes.filter((l) => !confirmes.includes(l.cle) && !retires.includes(l.cle) && !parEcole(l)).length;
  const fiche = (moi.decomptes || []).find((x) => x.mois === mois);

  const aFaire = lignes.filter((l) => !confirmes.includes(l.cle) && !retires.includes(l.cle) && !parEcole(l));
  const faites = valide ? lignes : lignes.filter((l) => confirmes.includes(l.cle) || retires.includes(l.cle) || parEcole(l));
  const totalRange = faites.filter((l) => !retires.includes(l.cle) && !parEcole(l)).reduce((x, l) => x + Number(l.montant || 0), 0);
  // Une ligne : à gauche, le jour, le cours et ses élèves (toute la largeur restante) ; à
  // droite, le montant AU-DESSUS des boutons ✗ / ✓ — côte à côte, ils écrasaient le nom du
  // cours sur trois lignes (« Comédie / musicale 7-9 / ans », iPhone de Jen, 01.11.2026).
  const rendreLigne = (l, range = false) => {
    const ok = confirmes.includes(l.cle);
    const ecole = parEcole(l);
    const non = retires.includes(l.cle) || ecole || (valide && l.retire);
    return (
      <div key={l.cle} className="px-4 py-3" data-testid="prof-ligne" style={non ? { background: "#FAFAFA" } : undefined}>
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1" style={non ? { opacity: 0.45 } : undefined}>
            <div className="text-[12px] text-[var(--muted)] capitalize">{jourCourt(l.date)}{l.debut ? ` · ${l.debut}–${l.fin}` : ""}</div>
            <div className="text-[15px] leading-snug" style={non ? { textDecoration: "line-through" } : undefined}>
              {l.cours}{l.remplace ? ` (remplace ${l.remplace})` : ""}
            </div>
            <div className="text-[12px] text-[var(--muted)]">
              {l.type === "cours privé" || l.type === "cours privé réservé" ? "cours privé" : `${eleves(l)} · ${l.montant_convenu ? "montant convenu" : `${l.tarif}.–${l.tarif_par === "cours" ? "/cours" : "/h"}`}`}
              {l.note ? ` · ${l.note}` : ""}
            </div>
            {partages && l.partage && !valide && !non && !l.corrige_ecole && (l.prevu ?? l.minutes) > 0 && (() => {
              // Sa part du cours (cours aux horaires partagés seulement : la comédie musicale) :
              // par défaut les minutes prévues ; par quart d'heure, jusqu'à la fin du cours
              // (1h30 pour un cours de 1h30). Pas sur un cours sans élève (0 min).
              const fin = (moi.cours || []).find((c) => c.id === l.cours_id)?.fin;
              const maxi = fin && l.debut ? enMin(fin) - enMin(l.debut) : l.duree_cours || 60;
              const prevu = l.prevu ?? l.minutes;
              const valeur = durees.current[l.cle] ?? prevu;
              const choix = [];
              for (let m = 15; m <= maxi; m += 15) choix.push(m);
              if (!choix.includes(prevu)) choix.push(prevu);
              // Sur téléphone, « Ma part du cours » s'écrivait un mot par ligne (test du
              // 01.11.2026) : le libellé reste d'un bloc, le menu passe dessous s'il le faut.
              return (
                <label className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12px] text-[var(--muted)]">
                  <span className="whitespace-nowrap">Ma part du cours</span>
                  <select value={valeur} onChange={(e) => annoncer(l, Number(e.target.value))} data-testid="prof-ligne-duree"
                    className="rounded-full border bg-white px-2 py-1 text-[12.5px] text-[var(--text)]"
                    style={{ borderColor: valeur !== prevu ? "#F59E0B" : "var(--border)" }}>
                    {choix.sort((a, b) => a - b).map((m) => <option key={m} value={m}>{m} min{m === prevu ? " (prévu)" : ""}</option>)}
                  </select>
                  {valeur !== prevu && (
                    <span className="inline-flex flex-wrap gap-x-1.5" style={{ color: "#B45309" }} data-testid="prof-ligne-duree-proposee">
                      <span className="whitespace-nowrap">proposé à l'école</span>
                      {l.debut && <span className="whitespace-nowrap tabular-nums">{l.debut}–{enHeure(enMin(l.debut) + valeur)}</span>}
                    </span>
                  )}
                </label>
              );
            })()}
          </div>
          <div className="shrink-0 flex flex-col items-end gap-2">
            <div className="text-[14.5px] font-semibold tabular-nums" style={non ? { opacity: 0.45 } : undefined}>{chf(non ? 0 : l.montant)}</div>
            <div className="flex items-center gap-2">
              {valide ? (!non && <Badge couleur="#16A34A">✓</Badge>) : ecole ? (
                // Retiré par l'école : le prof le voit, mais c'est l'école qui le remet.
                <span className="shrink-0 text-[12px] text-[var(--muted)]" data-testid="prof-ligne-retiree-ecole">retiré par l'école</span>
              ) : range ? (
                // Déjà rangé : un tap le remet « à confirmer » (ou remet un cours retiré).
                <button onClick={() => marquer(l, non ? "non" : "ok")} className="shrink-0 inline-flex items-center gap-1 text-[13px] font-medium min-h-[44px]"
                  style={{ color: non ? "var(--primary)" : "#16A34A" }} data-testid={non ? "prof-ligne-remettre" : "prof-ligne-annuler"}>
                  {non ? <><RotateCcw size={14} /> Remettre</> : <><Check size={16} strokeWidth={2.8} /> Donné</>}
                </button>
              ) : (
                <>
                  <button onClick={() => marquer(l, "non")} aria-label="Pas donné" data-testid="prof-ligne-retirer"
                    className="w-11 h-11 rounded-full grid place-items-center shrink-0" style={{ border: "2px solid #FECACA", color: "#DC2626" }}>
                    <X size={17} strokeWidth={2.6} />
                  </button>
                  <button onClick={() => marquer(l, "ok")} aria-label="Donné" data-testid="prof-ligne-ok"
                    className="w-11 h-11 rounded-full grid place-items-center shrink-0 transition-all"
                    style={ok ? { background: "#16A34A", color: "#fff" } : { border: "2px solid #BBF7D0", color: "#16A34A" }}>
                    <Check size={18} strokeWidth={2.8} />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };


  return (
    <div className="space-y-5" data-testid="prof-heures">
      <div className="flex items-center justify-between rounded-[22px] bg-white px-2 py-1.5" style={{ border: "1px solid var(--border)" }}>
        <button onClick={() => setMois(decalerMois(mois, -1))} aria-label="Mois précédent" className="w-11 h-11 grid place-items-center rounded-full"
          disabled={premier} style={premier ? { opacity: 0.25 } : undefined} data-testid="prof-mois-precedent"><ChevronLeft size={20} /></button>
        <div className="text-[16px] font-semibold capitalize">{moisLisible(mois)}</div>
        <button onClick={() => setMois(decalerMois(mois, 1))} aria-label="Mois suivant" className="w-11 h-11 grid place-items-center rounded-full"><ChevronRight size={20} /></button>
      </div>
      {erreur && <p className="text-sm" style={{ color: "#B91C1C" }}>{erreur}</p>}
      {premier && (
        <p className="text-[12.5px] text-[var(--muted)] px-1 -mt-2" data-testid="prof-heures-depuis">
          L'application compte tes heures depuis {moisLisible(des)}. Les mois d'avant ont été réglés avec l'école comme avant.
        </p>
      )}
      {!d ? <div className="py-16 grid place-items-center"><RefreshCw className="animate-spin" size={20} /></div> : horsApp ? (
        <div className="rounded-[22px] bg-white p-5 space-y-1" style={{ border: "1px solid var(--border)" }} data-testid="prof-heures-hors-app">
          <div className="text-[15px] font-semibold">Rien à faire pour ce mois</div>
          <p className="text-[13.5px] text-[var(--muted)]">
            Tes heures de {moisLisible(mois)} ont été faites à la main, avant l'application. L'app compte tes heures depuis {moisLisible(d.heures_app_des)}.
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-[22px] p-5 text-white" style={{ background: valide ? "linear-gradient(140deg,#14532D,#16A34A)" : "linear-gradient(140deg,#0F1B2D 0%,#123243 60%,#00A8AF 100%)" }}>
            <div className="text-[12px] uppercase tracking-[0.14em] font-semibold" style={{ color: valide ? "#BBF7D0" : "#66EAF0" }}>
              {valide ? "Validé par l'école" : d.statut === "confirme" ? "Envoyé · en attente de validation" : "À vérifier puis envoyer"}
            </div>
            <div className="font-display text-[34px] leading-tight mt-1 tabular-nums">{chf(valide ? d.total : total)}</div>
            <div className="text-[13px] mt-1" style={{ color: "rgba(255,255,255,0.8)" }}>
              {(valide ? d.heures : heures).toFixed(2)} h · brut{!valide && lignes.length ? ` · ${lignes.length - aVerifier}/${lignes.length} lignes vérifiées` : ""}
              {reguls.length > 0 && <span data-testid="prof-heures-dont-correction"> · dont correction {chfSigne(totalReguls)}</span>}
            </div>
            {valide && fiche && (
              <a href={urlServeur(fiche.pdf_lien)} target="_blank" rel="noopener noreferrer" data-testid="prof-pdf"
                className="mt-4 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13.5px] font-semibold" style={{ background: "#fff", color: "#14532D" }}>
                <FileText size={15} /> Ma fiche de paie (PDF)
              </a>
            )}
          </div>

          {!valide && aFaire.length > 0 && (
            <p className="text-[13px] text-[var(--muted)] px-1">
              Pour chaque cours : <strong style={{ color: "#16A34A" }}>✓</strong> donné, <strong style={{ color: "#DC2626" }}>✗</strong> pas donné — le cours se range dans « Confirmés », tu peux toujours le changer.
            </p>
          )}

          {lignes.length === 0 && <Vide>{futures.length ? "Aucun cours donné pour l'instant ce mois-ci."
            : vides.length ? "Aucun de tes cours n'avait d'élève ce mois-ci : rien à envoyer." : "Aucun cours ce mois-ci."}</Vide>}

          {/* À confirmer : les cours donnés pas encore cochés. Un ✓ ou un ✗ les range
              aussitôt plus bas, dans « Confirmés » : il ne reste ici que le travail à faire. */}
          {!valide && aFaire.length > 0 && (
            <section>
              <TitreGroupe>À confirmer · {aFaire.length} cours</TitreGroupe>
              <Groupe testId="prof-heures-lignes">{aFaire.map((l) => rendreLigne(l))}</Groupe>
            </section>
          )}
          {!valide && lignes.length > 0 && aFaire.length === 0 && (
            <div className="rounded-[22px] px-4 py-3 text-[14px] flex items-center gap-2" style={{ background: "#F0FDF4", color: "#15803D" }}>
              <Check size={16} /> Tous les cours donnés sont confirmés.
            </div>
          )}

          {faites.length > 0 && (
            <section>
              <TitreGroupe>
                {valide ? "Validés par l'école" : d.statut === "confirme" ? "Envoyés à l'école" : "Confirmés"} · {chf(totalRange)}
              </TitreGroupe>
              <Groupe testId="prof-heures-confirmees">{faites.map((l) => rendreLigne(l, true))}</Groupe>
            </section>
          )}

          {reguls.length > 0 && (
            <section>
              <TitreGroupe>{reguls.length > 1 ? "Corrections" : "Correction"} · {chfSigne(totalReguls)}</TitreGroupe>
              <Groupe testId="prof-heures-corrections">
                {reguls.map((l) => (
                  <div key={l.cle} className="flex items-start gap-3 px-4 py-3" data-testid="prof-ligne-correction">
                    <div className="min-w-0 flex-1">
                      {/* Le montant est à droite : pas deux fois dans la phrase. */}
                      <div className="text-[15px] leading-snug">{String(l.explication || l.cours).replace(/\s*\([−+][^)]*\)$/, "")}</div>
                      <div className="text-[12px] text-[var(--muted)]">{l.regularisation?.cours ? `${l.regularisation.cours} · ` : ""}{l.note}</div>
                    </div>
                    <div className="shrink-0 text-[14.5px] font-semibold tabular-nums" style={{ color: Number(l.montant) < 0 ? "#B91C1C" : "#15803D" }}>
                      {chfSigne(l.montant)}
                    </div>
                  </div>
                ))}
              </Groupe>
              <p className="px-1 mt-1.5 text-[12px] text-[var(--muted)]">
                Un mois déjà validé reste tel quel : l'école reporte la différence ici. Une question ? Appelle l'école.
              </p>
            </section>
          )}

          {vides.length > 0 && (
            <details className="rounded-[22px] bg-white px-4 py-3" style={{ border: "1px solid var(--border)" }} data-testid="prof-heures-sans-eleve">
              <summary className="text-[14px] text-[var(--muted)] cursor-pointer min-h-[28px]">
                Sans élève · {vides.length} cours, rien à cocher (pas payé{vides.length > 1 ? "s" : ""})
              </summary>
              <div className="mt-1">
                {vides.map((l) => (
                  <div key={l.cle} className="flex items-baseline justify-between gap-3 py-1.5 text-[13.5px] text-[var(--muted)]">
                    <span className="min-w-0"><span className="capitalize">{jourCourt(l.date)}</span> · {l.cours}</span>
                    <span className="shrink-0 tabular-nums">{l.debut ? `${l.debut}–${l.fin}` : ""}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {futures.length > 0 && (
            <section>
              <TitreGroupe>À venir · à valider une fois donnés</TitreGroupe>
              <Groupe testId="prof-heures-avenir">
                {futures.map((l) => (
                  <div key={l.cle} className="flex items-center gap-3 px-4 py-3" data-testid="prof-ligne-avenir">
                    <div className="min-w-0 flex-1">
                      <div className="text-[12px] text-[var(--muted)] capitalize">{jourCourt(l.date)}{l.debut ? ` · ${l.debut}–${l.fin}` : ""}</div>
                      <div className="text-[15px] leading-snug text-[var(--muted)]">{l.cours}{l.remplace ? ` (remplace ${l.remplace})` : ""}</div>
                    </div>
                    <div className="text-[14px] tabular-nums shrink-0 text-[var(--muted)]">{chf(l.montant)}</div>
                  </div>
                ))}
              </Groupe>
            </section>
          )}

          {/* Sous « À venir » (Jen, 02.11.2026) : ses cours d'abord, puis les séances où c'est
              le tour d'un collègue. */}
          {tours.length > 0 && (
            <section>
              <TitreGroupe>Alternance · le tour d'un collègue</TitreGroupe>
              <Groupe testId="prof-heures-alternance">
                {tours.map((t) => {
                  const passe = !aVenir(t);
                  const etat = t.reprise === "en_attente"
                    ? [`Proposé à l'école : donné à la place de ${t.autre}`, "#B45309"]
                    : t.reprise === "refusee" ? [`L'école a gardé ${t.autre}`, "var(--muted)"]
                      : [`${t.autre} ce jour-là`, "var(--muted)"];
                  return (
                    <div key={t.cle} className="flex items-center gap-3 px-4 py-3" data-testid="prof-ligne-alternance" style={{ background: "#FAFAFA" }}>
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] text-[var(--muted)] capitalize">{jourCourt(t.date)} · {t.debut}–{t.fin}</div>
                        <div className="text-[15px] leading-snug text-[var(--muted)]">{t.cours}</div>
                        <div className="text-[12px]" style={{ color: etat[1] }} data-testid="prof-ligne-alternance-etat">{etat[0]}</div>
                      </div>
                      {!valide && passe && t.reprise === "en_attente" && (
                        <button onClick={() => reprendre(t, true)} className="shrink-0 text-[13px] font-medium min-h-[44px] px-1"
                          style={{ color: "var(--muted)" }} data-testid="prof-alternance-annuler">Annuler</button>
                      )}
                      {!valide && passe && !t.reprise && (
                        <button onClick={() => reprendre(t)} className="shrink-0 rounded-full px-3.5 min-h-[40px] text-[13.5px] font-semibold"
                          style={{ border: "1.5px solid var(--primary)", color: "var(--primary)" }} data-testid="prof-alternance-reprendre">
                          Je l'ai donné
                        </button>
                      )}
                    </div>
                  );
                })}
              </Groupe>
              <p className="px-1 mt-1.5 text-[12px] text-[var(--muted)]">
                Vous avez échangé ? « Je l'ai donné » prévient l'école : elle bascule la séance, puis elle compte dans tes heures.
              </p>
            </section>
          )}

          {!valide && (ajout
            ? <Ajouter moi={moi} mois={mois} onAjout={ajouter} onFermer={() => setAjout(false)} />
            : (
              <button className="w-full rounded-[22px] bg-white py-4 text-[15px] font-semibold inline-flex items-center justify-center gap-2"
                style={{ border: "1.5px dashed #CBD5E1", color: "var(--primary)" }} onClick={() => setAjout(true)} data-testid="prof-ajouter">
                <Plus size={17} /> Ajouter un cours (privé ou autre)
              </button>
            ))}

          {!valide && (lignes.length > 0 || futures.length > 0) && (aVerifier > 0 || futures.length > 0 ? (
            // Pas encore d'envoi possible : une ligne d'information À SA PLACE, en bas de la
            // liste. Un bouton désactivé qui flottait par-dessus les cours les cachait
            // (iPhone de Jen, 01 et 02.11.2026).
            <div className="rounded-[22px] px-4 py-3.5 text-[14px] flex items-start gap-2.5" data-testid="prof-confirmer-attente"
              style={{ background: "#F1F5F9", color: "#475569" }}>
              <Send size={16} className="shrink-0 mt-0.5" />
              <span>{futures.length > 0 ? `Tu pourras envoyer tes heures après ton dernier cours du mois (${jourCourt(futures[futures.length - 1].date)}).`
                : `Encore ${aVerifier} ligne${aVerifier > 1 ? "s" : ""} à vérifier (✓ donné ou ✗ pas donné), puis « Envoyer mes heures ».`}</span>
            </div>
          ) : (
            // Prêt à partir : le bouton flotte au-dessus de la barre d'onglets (fixe, ~80 px +
            // la zone de l'iPhone), sur un fond qui masque les lignes qui défilent dessous.
            <div className="sticky -mx-4 px-4 pt-3 pb-1" style={{ bottom: "calc(5.25rem + env(safe-area-inset-bottom, 0px))",
              background: "linear-gradient(to bottom, rgba(244,247,250,0), rgba(244,247,250,0.96) 35%)" }}>
              <button onClick={envoyer} className="btn-primary w-full justify-center shadow-lg" data-testid="prof-confirmer">
                <Send size={15} /> {d.statut === "confirme" ? `Renvoyer mes heures (${chf(total)})` : `Envoyer mes heures (${chf(total)})`}
              </button>
            </div>
          ))}
          {message && <p className="text-sm" style={{ color: "#15803D" }}>{message}</p>}
          {annuler && (
            <div className="fixed left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-full px-4 py-2.5 text-white text-[13.5px] shadow-lg"
              style={{ bottom: "calc(6.5rem + env(safe-area-inset-bottom, 0px))", background: "#0F1B2D" }}>
              « {annuler.cours} » : pas donné
              <button className="font-semibold" style={{ color: "#66EAF0" }} onClick={() => { marquer(annuler, "non"); setAnnuler(null); }}>Annuler</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
